import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  CreditCard,
  InfoIcon,
  Zap,
  Bot,
  MessageSquare,
  Twitter,
  Image,
  Sparkles,
  BarChart3,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAgentCreditBalance } from "@/hooks/use-agent-credit-balance";
import { useProductPricing } from "@/hooks/use-product-pricing";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

interface CreditWarningModalProps {
  agentId: string;
  onClose?: () => void;
  minimumCredits?: number;
  showDebugInfo?: boolean;
}

export function CreditWarningModal({
  agentId,
  onClose,
  minimumCredits = 10,
  showDebugInfo = false,
}: CreditWarningModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    credits,
    isLoading: isLoadingCredits,
    error,
  } = useAgentCreditBalance(agentId);
  const {
    products,
    productsByFamily,
    isLoading: isLoadingProducts,
  } = useProductPricing();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>("overview");

  // Debug product data
  useEffect(() => {
    if (!isLoadingProducts && products.length > 0) {
      const families = [...new Set(products.map((p) => p.product_family))];
      console.debug("Available product families:", families);
      console.debug("Products by family:", productsByFamily);
    }
  }, [isLoadingProducts, products, productsByFamily]);

  // Check if agent has insufficient credits when component mounts or agentId changes
  useEffect(() => {
    if (!isLoadingCredits && agentId && credits !== undefined) {
      const hasLowCredits = credits < minimumCredits;
      console.debug(
        `Agent ${agentId} has ${credits} credits (minimum: ${minimumCredits})`,
      );
      setIsOpen(hasLowCredits);
    }
  }, [agentId, credits, isLoadingCredits, minimumCredits]);

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  const handleNavigateToCredits = () => {
    navigate(`/account#credits`);
    setIsOpen(false);
  };

  const handleNavigateToAutomation = () => {
    navigate(`/autonomous#agents`);
    setIsOpen(false);
  };

  const handleNavigateToAnalytics = () => {
    navigate(`/account#analytics`);
    setIsOpen(false);
  };

  // Get product categories - using most common product families from observed data
  const isLoading = isLoadingCredits || isLoadingProducts;

  // Get all product families
  const productFamilies = !isLoadingProducts
    ? Object.keys(productsByFamily).sort()
    : [];

  // Don't render anything while loading or if we have sufficient credits or no agent ID
  if (isLoadingCredits || !agentId || credits >= minimumCredits) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-full max-w-[60vw] lg:max-w-[60vw] max-h-[70vh] overflow-y-auto">
        <DialogHeader className="flex-row items-center gap-4">
          <div className="bg-yellow-500/20 p-3 rounded-full">
            <AlertCircle className="h-8 w-8 text-yellow-500" />
          </div>
          <div>
            <DialogTitle className="text-2xl">Low Credit Balance</DialogTitle>
            <DialogDescription className="text-base">
              Your agent has insufficient credits to perform operations.
            </DialogDescription>
          </div>
        </DialogHeader>

        <Tabs
          defaultValue="overview"
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full mt-4"
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview" className="text-base py-3">
              Overview
            </TabsTrigger>
            <TabsTrigger value="details" className="text-base py-3">
              Usage
            </TabsTrigger>
            <TabsTrigger value="pricing" className="text-base py-3">
              Pricing
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6 pt-6">
            <div className="flex flex-col md:flex-row md:items-center gap-6 pb-6 px-2 border-b">
              <div className="flex items-center gap-4">
                <CreditCard className="h-14 w-14 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-base text-muted-foreground">
                    Current Balance
                  </p>
                  <div className="flex items-center">
                    <p className="text-3xl font-semibold">{credits} credits</p>
                    {showDebugInfo && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <InfoIcon className="ml-2 h-5 w-5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-xs">
                            <p>Agent ID: {agentId}</p>
                            {error && (
                              <p className="text-red-400">
                                Error: {String(error)}
                              </p>
                            )}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>

              <div className="ml-auto p-3 bg-primary/10 rounded-lg">
                <p className="text-base font-medium text-primary">
                  Credit Value: 1 credit = $0.01 USD
                </p>
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-6 space-y-4">
              <h3 className="text-xl font-medium flex items-center gap-2">
                <Zap className="h-5 w-5 text-yellow-500" />
                What are Credits?
              </h3>
              <p className="text-base text-muted-foreground">
                Credits power all agent operations on the platform. They enable
                your agents to create content, respond to messages, and engage
                autonomously with others online.
              </p>
              <p className="text-base text-muted-foreground">
                Each action your agent performs consumes a specific number of
                credits based on the complexity and computational resources
                required. Credits are valued at $0.01 USD per credit.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="details" className="space-y-6 pt-6">
            <h3 className="text-xl font-medium px-2">Credit Usage Examples:</h3>

            <div className="space-y-5">
              <div className="flex items-start gap-5 border-b pb-5 px-2">
                <div className="bg-primary/10 p-3 rounded-full">
                  <Bot className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-lg font-medium">Autonomous Actions</p>
                  <p className="text-base text-muted-foreground">
                    Automated content creation and posting to Twitter requires
                    credits for each operation. Higher quality, longer form
                    content requires more credits.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-5 border-b pb-5 px-2">
                <div className="bg-primary/10 p-3 rounded-full">
                  <MessageSquare className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-lg font-medium">
                    Interactions & Responses
                  </p>
                  <p className="text-base text-muted-foreground">
                    Your agent uses credits when replying to mentions, direct
                    messages, or engaging with content. Credits provide your
                    agent with the ability to think and respond intelligently.
                  </p>
                </div>
              </div>

              <div className="mt-6 px-2">
                <Button
                  variant="link"
                  className="p-0 h-auto text-primary text-base"
                  onClick={handleNavigateToAutomation}
                >
                  View full pricing and usage breakdown →
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="pricing" className="space-y-6 pt-6">
            <h3 className="text-xl font-medium px-2">Product Credit Costs:</h3>

            {isLoading ? (
              <div className="text-center text-base text-muted-foreground py-6">
                Loading product pricing...
              </div>
            ) : products.length === 0 ? (
              <div className="text-center text-base text-muted-foreground py-6">
                No product pricing information available.
              </div>
            ) : (
              <div className="space-y-8">
                {/* Display products grouped by family */}
                {productFamilies.map((family) => {
                  const familyProducts = productsByFamily[family] || [];
                  if (familyProducts.length === 0) return null;

                  // Select appropriate icon based on family name
                  let FamilyIcon = Sparkles;
                  if (family.toLowerCase().includes("twitter"))
                    FamilyIcon = Twitter;
                  if (family.toLowerCase().includes("image"))
                    FamilyIcon = Image;

                  return (
                    <div key={family} className="px-2">
                      {/* Family Header */}
                      <div className="flex items-center gap-3 mb-4 border-b pb-2">
                        <div className="bg-primary/10 p-2 rounded-md">
                          <FamilyIcon className="h-6 w-6 text-primary" />
                        </div>
                        <h4 className="text-lg font-medium capitalize">
                          {family.replace(/_/g, " ")}
                        </h4>
                      </div>

                      {/* Products Grid - 4 columns */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pl-4">
                        {familyProducts.map((product) => (
                          <div
                            key={product.product_sku}
                            className="border border-border rounded-md p-3 hover:bg-muted/30 transition-colors"
                          >
                            <div className="flex justify-between items-center mb-2">
                              <h5 className="font-medium text-base leading-tight mb-1 line-clamp-2">
                                {product.name}
                              </h5>
                              <Badge className="ml-auto whitespace-nowrap">
                                {product.credit_cost} credits
                              </Badge>
                            </div>
                            {product.description && (
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {product.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <div className="text-center mt-8 pb-2">
                  <Button
                    variant="link"
                    className="p-0 h-auto text-primary text-base"
                    onClick={handleNavigateToAutomation}
                  >
                    View complete product catalog →
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="sm:justify-start gap-4 pt-6">
          <Button
            type="button"
            variant="default"
            size="lg"
            onClick={handleNavigateToCredits}
            className="text-base"
          >
            <CreditCard className="h-5 w-5 mr-2" />
            Add Credits
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={handleNavigateToAnalytics}
            className="text-base"
          >
            <BarChart3 className="h-5 w-5 mr-2" />
            View Usage
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={handleClose}
            className="text-base"
          >
            Continue Anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreditWarningModal;
